import { Typography, LinearProgress } from "@mui/material";
import { Box } from "@mui/system";
import { React, useState } from "react";
import { useDispatch } from "react-redux";
import { apiSlice } from "../../../app/apiSlice";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import StaffTable from "./StaffTable";
import EditStaffDialog from "./EditStaffDialog";
import { useGetTeachersPaginatedQuery, useDeleteTeacherMutation } from "./staffApiSlice";
import { useAdminResetPasswordMutation, useToggleUserStatusMutation } from "../../../app/authApiSlice";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import toast from "../../../helpers/toast";
import { useGetSchoolProfileQuery } from "../settings/school/schoolApiSlice";

function StaffList() {
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [selectedStaffForReset, setSelectedStaffForReset] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const [deleteTeacher] = useDeleteTeacherMutation();
  const [adminResetPassword] = useAdminResetPasswordMutation();
  const [toggleUserStatus] = useToggleUserStatusMutation();
  const dispatch = useDispatch();
  const { data: schoolInfo } = useGetSchoolProfileQuery();

  const {
    data: teachersData,
    isLoading: getTeachersLoading,
    error: getTeachersError,
  } = useGetTeachersPaginatedQuery({ page, per_page: perPage, q: searchQuery });

  const teachers = teachersData?.teachers || teachersData || [];
  const pagination = teachersData?.pagination;

  const handleDelete = async (item) => {
    try {
      const response = await deleteTeacher(item).unwrap();
      if (response) {
        toast.success("Teacher deleted successfully");
      }
    } catch (error) {
    }
  };

  const handleReset = async (item) => {
    try {
      await adminResetPassword(item?.user_id).unwrap();
      toast.success("Password reset to default successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to reset password");
    }
  };

  const handleToggleStatus = async (item) => {
    const queryArgs = { page, per_page: perPage, q: searchQuery };
    const patchResult = dispatch(
      apiSlice.util.updateQueryData("getTeachersPaginated", queryArgs, (draft) => {
        const list = draft?.teachers || draft;
        if (Array.isArray(list)) {
          const teacher = list.find((t) => t.user?.id === item.user?.id);
          if (teacher?.user) teacher.user.is_active = !teacher.user.is_active;
        }
      })
    );
    try {
      await toggleUserStatus(item?.user?.id).unwrap();
    } catch (error) {
      patchResult.undo();
      toast.error(error?.message || "Failed to update status");
    }
  };

  if (getTeachersError) {
    toast.error(getTeachersError.message || "something went wrong");
  }


  return (
    <>
      <StyledPaper>
        <CustomWarningDialog
          open={openWarningDialog}
          setOpen={setOpenWarningDialog}
          item={selectedStaff}
          action={handleDelete}
          message="Are you sure you want to delete this teacher?"
        />
        <CustomWarningDialog
          open={openResetDialog}
          setOpen={setOpenResetDialog}
          item={selectedStaffForReset}
          action={handleReset}
          message="Reset this teacher's password to the default? They will be prompted to change it on next login."
        />
        <EditStaffDialog
          open={openEdit}
          setOpen={setOpenEdit}
          selectedStaff={selectedStaff}
        />

        <Box className="paperHeader">
          <Typography variant="formHeading">Teachers</Typography>
          <Typography variant="formSubHeading" sx={{ textTransform: "none" }}>
            Click on a staff to view and modify their information
          </Typography>
        </Box>
        {getTeachersLoading && <LinearProgress />}
        <StaffTable
          schoolInfo={schoolInfo}
          rows={teachers}
          pagination={pagination}
          page={page}
          perPage={perPage}
          searchQuery={searchQuery}
          onPageChange={setPage}
          onPerPageChange={(val) => { setPerPage(val); setPage(1); }}
          onSearchChange={(val) => { setSearchQuery(val); setPage(1); }}
          onToggleStatus={handleToggleStatus}
          setSelectedStaff={setSelectedStaff}
          setOpenEdit={setOpenEdit}
          setOpenWarningDialog={setOpenWarningDialog}
          setSelectedStaffForReset={setSelectedStaffForReset}
          setOpenResetDialog={setOpenResetDialog}
        />
      </StyledPaper>
    </>
  );
}

export default StaffList;
